import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  CircularProgress,
  styled,
} from "@mui/material";
import { Close, CloudUpload, Delete } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Setting } from "../../utils/Setting";
import authActions from "../../redux/reducers/auth/actions";

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: "10px 24px",
  letterSpacing: "0",
  textTransform: "none",
  "&:hover": {
    background: "#3A5AE0",
    border: "1px solid #3A5AE0",
  },
  "&:disabled": {
    background: "#B0B0B0",
    border: "1px solid #B0B0B0",
  },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: "10px 24px",
  letterSpacing: "0",
  textTransform: "none",
  "&:hover": {
    background: "#F5F5F5",
  },
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #D1D5DB",
  borderRadius: "12px",
  padding: "32px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  backgroundColor: "#F9FAFB",
  "&:hover": {
    borderColor: "#4D6BF6",
    backgroundColor: "#F0F4FF",
  },
}));

export default function ProfilePhotoModal({
  visible = false,
  handleClose = () => null,
  currentPhoto = "",
  onUploadSuccess = () => null,
  username = "",
}) {
  const { token, userData } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { setUserData } = authActions;

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploading(false);
    }
  }, [visible]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an image first");
      return;
    }

    setUploading(true);

    try {
      const contractorData = userData?.contractor_data || {};
      console.log("contractorData:", contractorData);
      
      // Build FormData with the file
      const formData = new FormData();
      formData.append("company_name", contractorData?.company_name || "");
      formData.append("description", contractorData?.description || "");
      formData.append("website", contractorData?.website || "");
      formData.append("total_years_in_business", contractorData?.total_years_in_business || "");
      formData.append("company_size", contractorData?.company_size || "");
      formData.append("no_of_contracts_annually", contractorData?.no_of_contracts_annually || "");
      formData.append("contractor_expertise", contractorData?.contractor_expertise || "");
      formData.append("linkedin_url", contractorData?.linkedin_url || "");
      formData.append("fb_url", contractorData?.fb_url || "");
      formData.append("insta_url", contractorData?.insta_url || "");
      formData.append("company_address", contractorData?.company_address || "");
      formData.append("lat", contractorData?.lat ? String(contractorData.lat) : "");
      formData.append("long", contractorData?.long ? String(contractorData.long) : "");
      formData.append("city", contractorData?.city || "");
      formData.append("type", contractorData?.type || userData?.role || "contractor");
      formData.append("projects_key_locations", contractorData?.projects_key_locations || "");
      formData.append("business_logo", selectedFile); // Send the actual file

      console.log("Uploading profile photo...");
      const url = Setting.api + Setting.endpoints.addContractorDetails;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const responseData = await response.json();
      console.log("Response:", responseData);

      if (responseData && responseData.success) {
        toast.success("Profile photo updated successfully");
        dispatch(setUserData(responseData?.data));
        onUploadSuccess(responseData?.data);
        handleCloseModal();
      } else {
        toast.error(responseData?.message || "Failed to update profile photo");
      }
    } catch (error) {
      console.error("Profile photo upload error:", error);
      toast.error(error?.message || "An error occurred while uploading profile photo");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploading(false);
    handleClose();
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayUrl = previewUrl || currentPhoto;

  return (
    <Modal
      open={visible}
      onClose={handleCloseModal}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#FFF",
          borderRadius: "16px",
          padding: "24px",
          width: "90%",
          maxWidth: "480px",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1F2937",
              fontFamily: "Inter Tight",
            }}
          >
            Update Profile Photo
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Preview Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            src={displayUrl}
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              fontSize: "3rem",
            }}
          >
            {username?.charAt(0) || "U"}
          </Avatar>

          {previewUrl && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                startIcon={<Delete />}
                onClick={clearSelection}
                sx={{
                  textTransform: "none",
                  color: "#EF4444",
                }}
              >
                Remove
              </Button>
            </Box>
          )}
        </Box>

        {/* Upload Area */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        <UploadBox onClick={() => fileInputRef.current?.click()}>
          <CloudUpload sx={{ fontSize: 40, color: "#9CA3AF", mb: 1 }} />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#4B5563",
              mb: 0.5,
            }}
          >
            {selectedFile ? selectedFile.name : "Click to upload new photo"}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#9CA3AF",
            }}
          >
            PNG, JPG up to 5MB
          </Typography>
        </UploadBox>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 3,
            justifyContent: "flex-end",
          }}
        >
          <GreyStyledButton onClick={handleCloseModal} disabled={uploading}>
            Cancel
          </GreyStyledButton>
          <StyledButton
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startIcon={
              uploading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : null
            }
          >
            {uploading ? "Uploading..." : "Upload Photo"}
          </StyledButton>
        </Box>
      </Box>
    </Modal>
  );
}

